import React, { useState, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { Svg, Path } from 'react-native-svg';

const ChevronRightIcon = ({ color = "#A19F97" }: { color?: string }) => (
  <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <Path d="M5.83325 3.33325L10.4999 7.99992L5.83325 12.6666" stroke={color} strokeWidth="2" strokeLinecap="square" />
  </Svg>
);
import BottomSheet from '@gorhom/bottom-sheet';
import FloatingActionButton from '@/components/FloatingActionButton';
import TestNotificationButton from '@/components/TestNotificationButton';
import MeetingDetailsDrawer from '@/components/MeetingDetailsDrawer';
import { MeetingsService } from '@/services/meetingsService';
import { Meeting as DatabaseMeeting } from '@/types/meetings';
import { useLanguage } from '@/contexts/LanguageContext';

interface Meeting {
  id: string;
  title: string;
  description: string;
  time: string;
  participants: number;
  maxParticipants: number;
  type: 'group' | 'workshop' | 'meditation';
}

const initialMeetings: Meeting[] = [
  {
    id: '1',
    title: 'Círculo de Apoio Matinal',
    description: 'Começe o dia com força e propósito junto com outros em recuperação',
    time: '08:00',
    participants: 12,
    maxParticipants: 15,
    type: 'group'
  },
  {
    id: '2',
    title: 'Workshop: Lidando com Gatilhos',
    description: 'Aprenda estratégias práticas para identificar e gerenciar gatilhos',
    time: '14:30',
    participants: 8,
    maxParticipants: 10,
    type: 'workshop'
  },
  {
    id: '3',
    title: 'Meditação e Mindfulness',
    description: 'Sessão guiada de meditação para paz interior e clareza mental',
    time: '19:00',
    participants: 6,
    maxParticipants: 12,
    type: 'meditation'
  }
];

export default function EncontrosScreen() {
  const { t } = useLanguage();
  const [meetings, setMeetings] = useState<Meeting[]>(initialMeetings);
  const [selectedMeeting, setSelectedMeeting] = useState<DatabaseMeeting | null>(null);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const bottomSheetRef = useRef<BottomSheet>(null);

  const handleMeetingAdded = async () => {
    // Refresh meetings list from database
    try {
      const meetingsData = await MeetingsService.getAllMeetings();
      const updatedMeetings: Meeting[] = meetingsData.map(meeting => ({
        ...meeting,
        participants: 0, // Mock participants for UI
      }));
      setMeetings(updatedMeetings);
    } catch (error) {
      console.error('Error refreshing meetings:', error);
    }
  };

  // Convert local meeting to database meeting format for drawer
  const convertToDbMeeting = (meeting: Meeting): DatabaseMeeting => ({
    id: meeting.id,
    meeting_name: meeting.title,
    meeting_description: meeting.description,
    meeting_time: meeting.time,
    meeting_category: meeting.type,
    meeting_place: 'Google Meet', // Default for these demo meetings
    meeting_link: 'https://meet.google.com/demo-link',
    meeting_weekday: 'Segunda-feira; Terça-feira; Quarta-feira; Quinta-feira; Sexta-feira',
    meeting_password: '',
    meeting_region: '',
    meeting_period: '',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });

  const handleMeetingPress = (meeting: Meeting) => {
    const dbMeeting = convertToDbMeeting(meeting);
    setSelectedMeeting(dbMeeting);
    setIsDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerVisible(false);
    setSelectedMeeting(null);
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'group':
        return 'people';
      case 'workshop':
        return 'school';
      case 'meditation':
        return 'leaf';
      default:
        return 'calendar';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'group':
        return '#3B82F6';
      case 'workshop':
        return '#10B981';
      case 'meditation':
        return '#8B5CF6';
      default:
        return '#151515';
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{t('meetings.title')}</Text>
        <Text style={styles.headerSubtitle}>{t('meetings.subtitle')}</Text>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('meetings.today')}</Text>
          {meetings.slice(0, 10).map((meeting) => (
            <TouchableOpacity 
              key={meeting.id} 
              style={styles.meetingCard}
              onPress={() => handleMeetingPress(meeting)}
            >
              <View style={styles.meetingHeader}>
                <View style={[styles.typeIcon, { backgroundColor: getTypeColor(meeting.type) }]}>
                  <Ionicons name={getTypeIcon(meeting.type) as any} size={20} color="white" />
                </View>
                <View style={styles.meetingInfo}>
                  <Text style={styles.meetingTitle}>{meeting.title}</Text>
                  <Text style={styles.meetingTime}>{meeting.time}</Text>
                </View>
                <View style={styles.participantCount}>
                  <Text style={styles.participantText}>
                    {meeting.participants}/{meeting.maxParticipants}
                  </Text>
                </View>
              </View>
              <Text style={styles.meetingDescription}>{meeting.description}</Text>
              <View style={styles.joinButton}>
                <Text style={styles.joinButtonText}>{t('meetings.join')}</Text>
              </View>
            </TouchableOpacity>
          ))}
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('meetings.support')}</Text>

          <TouchableOpacity style={styles.resourceCard}>
            <View style={styles.resourceIcon}>
              <Ionicons name="call" size={24} color="#151515" />
            </View>
            <View style={styles.resourceContent}>
              <Text style={styles.resourceTitle}>{t('meetings.supportLine')}</Text>
              <Text style={styles.resourceDescription}>{t('meetings.supportLineDesc')}</Text>
            </View>
            <ChevronRightIcon />
          </TouchableOpacity>

          <TouchableOpacity style={styles.resourceCard}>
            <View style={styles.resourceIcon}>
              <Ionicons name="library" size={24} color="#151515" />
            </View>
            <View style={styles.resourceContent}>
              <Text style={styles.resourceTitle}>Biblioteca de Recursos</Text>
              <Text style={styles.resourceDescription}>Artigos, vídeos e ferramentas de apoio</Text>
            </View>
            <ChevronRightIcon />
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Floating Action Button for Admin Users */}
      <FloatingActionButton />
      
      {/* Test Notification Button */}
      <TestNotificationButton />

      {/* Meeting Details Drawer */}
      <MeetingDetailsDrawer
        ref={bottomSheetRef}
        isVisible={isDrawerVisible}
        onClose={handleCloseDrawer}
        meeting={selectedMeeting}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
    position: 'relative',
  },
  header: {
    paddingTop: 50,
    paddingHorizontal: 20,
    paddingBottom: 20,
    backgroundColor: '#F9F8F3',
  },
  headerTitle: {
    fontSize: 28,
    fontFamily: 'Sentient-Bold',
    color: '#151515',
    textAlign: 'center',
  },
  headerSubtitle: {
    fontSize: 16,
    fontFamily: 'Inter_400Regular',
    color: '#A19F97',
    textAlign: 'center',
    marginTop: 4,
  },
  content: {
    flex: 1,
    paddingHorizontal: 20,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontFamily: 'Sentient-Medium',
    color: '#151515',
    marginBottom: 16,
  },
  meetingCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  meetingHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  meetingTitle: {
    fontSize: 18,
    fontFamily: 'Sentient-Medium',
    color: '#151515',
    flex: 1,
  },
  meetingTime: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#A19F97',
  },
  meetingDescription: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#A19F97',
    marginBottom: 12,
    lineHeight: 20,
  },
  meetingFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  participantCount: {
    alignItems: 'center',
  },
  participantText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#A19F97',
  },
  meetingInfo: {
    flex: 1,
    marginHorizontal: 12,
  },
  typeIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  joinButton: {
    backgroundColor: '#151515',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  joinButtonText: {
    color: '#F9F8F3',
    fontSize: 14,
    fontFamily: 'Inter_600SemiBold',
  },
  resourceCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  resourceIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F9F8F3',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  resourceContent: {
    flex: 1,
  },
  resourceTitle: {
    fontSize: 16,
    fontFamily: 'Sentient-Medium',
    color: '#151515',
    marginBottom: 2,
  },
  resourceDescription: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#A19F97',
  },
  content: {
    flex: 1,
    paddingHorizontal: 20,
  },
  section: {
    marginBottom: 24,
  },
});